// The layout of the treemap: the response, the class the treemap is drilled
// into and the measure go in; the rectangles come out. Pure and
// deterministic: the same input gives the same rectangles, and nothing here
// knows of React or of the DOM.
//
// - The tree is rebuilt from `parent_id`. A class with classes beneath it is
//   an inner node, drawn as a frame with a header band; the rest are tiles.
// - A frame is drawn as one only on the first two levels and only where it
//   is large enough to hold tiles that read. Otherwise the class is a tile
//   of its own, and what lies beneath it is a drill away.
// - A tile is sized by the measure. A gap, or a class the measure counts as
//   zero, gets a small minimum so that it still shows; together the zeros of
//   a frame never take more than a fixed share of it, however many they are.
//   A gap holds no product, so that area says nothing: the view lays the
//   response out without its gaps (`withoutGaps`) unless they are asked for.
// - Products filed on a class directly (its count less its children's) are
//   drawn as a synthetic leaf of their own, "filed here", so that areas stay
//   honest: a frame is as large as the products it counts.
// - Every rectangle is whole pixels and lies inside the content box of its
//   frame. One too small to draw is left out and counted.

import { hierarchy, treemap, treemapSquarify } from "d3";

import { Treemap, TreemapMeasure, TreemapNode } from "../../../types/analytics";

// The label of the synthetic leaf of the products filed on a class itself
export const OWN_LABEL = "filed here";

// The padding of the box, the height of the header band of a frame, the
// padding inside one, and the gaps between first-level frames and between
// the rest
export const CANVAS_PADDING = 6;
export const FRAME_STRIP = 22;
export const FRAME_PADDING = 6;
export const GROUP_GAP = 6;
export const SIBLING_GAP = 4;

// A gap or a zero is sized as this share of the whole, at most
export const MINIMUM_SHARE = 0.012;
// The gaps and zeros of one frame take this share of it together, at most
export const ZERO_SHARE = 0.12;

// Frames nest this deep; a class further down is a tile
export const MAX_FRAME_DEPTH = 2;
// Below this a frame cannot hold tiles that read, and is a tile itself
export const FRAME_MIN_WIDTH = 120;
export const FRAME_MIN_HEIGHT = 84;
// Below this, in either direction, a rectangle is not drawn
export const MIN_DRAWN = 3;

// Below this a label does not fit and is left out
export const LEAF_LABEL_MIN_WIDTH = 52;
export const LEAF_LABEL_MIN_HEIGHT = 28;
export const LEAF_COUNT_MIN_HEIGHT = 46;
export const FRAME_LABEL_MIN_WIDTH = 56;
export const FRAME_COUNT_MIN_WIDTH = 96;

// How a rectangle is coloured under each colour mode. `products` and
// `makers` are scaled 0..1 against the largest leaf of the view; `domestic`
// is the class's share, or null where there is none.
export interface TreemapColourValues {
  products: number;
  makers: number;
  domestic: number | null;
}

export interface TreemapRect {
  // The class id; `own:<class id>` for the synthetic leaf
  key: string;
  // The class this rectangle is of; for the synthetic leaf, the class whose
  // products it holds
  nodeId: string;
  // The key of the frame it sits in; null on the first level
  parentKey: string | null;
  // 0 on the first level
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  // Products the rectangle counts
  count: number;
  makers: number;
  articles: number;
  // The measure's value it is sized by (before any minimum)
  value: number;
  // A frame: its classes are drawn inside it
  inner: boolean;
  // It has classes beneath it, drawn or not: it can be drilled into
  drillable: boolean;
  // The synthetic leaf of the products filed on a class itself
  own: boolean;
  gap: boolean;
  thin: boolean;
  other: boolean;
  colour: TreemapColourValues;
}

export interface TreemapLayout {
  rects: TreemapRect[];
  // The class the layout is drilled into; null at the top
  root: TreemapNode | null;
  // From the top down to `root`; empty at the top
  path: TreemapNode[];
  // Rectangles too small to draw in this box, left out of `rects`
  hidden: number;
}

export const measureOf = (node: TreemapNode, measure: TreemapMeasure): number => {
  switch (measure) {
    case "makers":
      return node.maker_count;
    case "articles":
      return node.articles;
    default:
      return node.product_count;
  }
};

export interface TreemapIndex {
  byId: Map<string, TreemapNode>;
  // The children of a class in the response's order; the top level is
  // under `null`
  children: Map<string | null, TreemapNode[]>;
}

export const indexTreemap = (data: Treemap): TreemapIndex => {
  const byId = new Map(data.nodes.map((node) => [node.id, node]));
  const children = new Map<string | null, TreemapNode[]>();
  children.set(
    null,
    data.root_ids.flatMap((id) => {
      const node = byId.get(id);
      return node ? [node] : [];
    }),
  );
  for (const node of data.nodes) {
    if (node.parent_id !== null && byId.has(node.parent_id)) {
      const siblings = children.get(node.parent_id) ?? [];
      siblings.push(node);
      children.set(node.parent_id, siblings);
    }
  }
  return { byId, children };
};

// The response without its gaps, the classes with nothing on record. Only
// gaps lie beneath a gap, so no counted class goes with one.
export const withoutGaps = (data: Treemap): Treemap => {
  const nodes = data.nodes.filter((node) => !node.gap);
  const kept = new Set(nodes.map((node) => node.id));
  return { ...data, nodes, root_ids: data.root_ids.filter((id) => kept.has(id)) };
};

// The classes with nothing on record beneath a class (the top where null):
// those of the response and those it left out. A gap counts once, whatever
// lies beneath it.
export const gapsBeneath = (
  data: Treemap,
  index: TreemapIndex,
  root: TreemapNode | null,
): number => {
  const within = (id: string | null): number =>
    (index.children.get(id) ?? []).reduce(
      (total, node) => total + (node.gap ? 1 : node.omitted_gaps + within(node.id)),
      0,
    );
  return (root ? root.omitted_gaps : data.omitted_root_gaps) + within(root ? root.id : null);
};

// From the top down to the node, or null where the id names none
export const pathTo = (index: TreemapIndex, id: string | null): TreemapNode[] | null => {
  if (id === null) {
    return [];
  }
  const path: TreemapNode[] = [];
  const seen = new Set<string>();
  let current = index.byId.get(id);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parent_id === null ? undefined : index.byId.get(current.parent_id);
  }
  return path.length > 0 && path[0].parent_id === null ? path : null;
};

// The products of a class that are filed on it and on none of its classes
export const ownCount = (index: TreemapIndex, node: TreemapNode): number => {
  const kids = index.children.get(node.id) ?? [];
  if (kids.length === 0) {
    return 0;
  }
  return Math.max(
    0,
    node.product_count - kids.reduce((total, kid) => total + kid.product_count, 0),
  );
};

// Counted classes first, by value, then `other`, then gaps
const order = (
  nodes: TreemapNode[],
  measure: TreemapMeasure,
): TreemapNode[] => {
  const kind = (node: TreemapNode) => (node.gap ? 2 : node.other ? 1 : 0);
  return nodes
    .map((node, position) => ({ node, position }))
    .sort(
      (a, b) =>
        kind(a.node) - kind(b.node) ||
        measureOf(b.node, measure) - measureOf(a.node, measure) ||
        a.position - b.position,
    )
    .map(({ node }) => node);
};

interface Draft {
  key: string;
  node: TreemapNode;
  own: boolean;
  // The measure's value of a leaf; unset on a frame
  value: number;
  // The value summed over what lies beneath, and the share it is laid out by
  real: number;
  weight: number;
  drillable: boolean;
  children?: Draft[];
}

const leafValue = (node: TreemapNode, measure: TreemapMeasure): number =>
  node.gap ? 0 : Math.max(0, measureOf(node, measure));

const sumReal = (draft: Draft): number => {
  draft.real = draft.children
    ? draft.children.reduce((total, child) => total + sumReal(child), 0)
    : draft.value;
  return draft.real;
};

// Shares `weight` among the drafts by their values. Those worth nothing get
// `minimum` each, but never more than `ZERO_SHARE` of the whole together.
const share = (drafts: Draft[], weight: number, minimum: number): void => {
  const real = drafts.reduce((total, draft) => total + draft.real, 0);
  const zeros = drafts.filter((draft) => draft.real <= 0).length;
  const zeroTotal =
    real > 0 ? Math.min(zeros * minimum, (real * ZERO_SHARE) / (1 - ZERO_SHARE)) : 0;
  for (const draft of drafts) {
    if (real <= 0) {
      draft.weight = weight / drafts.length;
    } else if (draft.real <= 0) {
      draft.weight = (weight * zeroTotal) / zeros / (real + zeroTotal);
    } else {
      draft.weight = (weight * draft.real) / (real + zeroTotal);
    }
    if (draft.children) {
      share(draft.children, draft.weight, minimum);
    }
  }
};

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

// The layout of the classes beneath `rootId` (the top where null) in a box
// of the given size. An unknown root is read as the top.
export const layoutTreemap = (
  data: Treemap,
  rootId: string | null,
  measure: TreemapMeasure,
  width: number,
  height: number,
): TreemapLayout => {
  const index = indexTreemap(data);
  const path = pathTo(index, rootId) ?? [];
  const root = path.length > 0 ? path[path.length - 1] : null;

  const build = (node: TreemapNode): Draft => {
    const kids = index.children.get(node.id) ?? [];
    const draft = { key: node.id, node, own: false, real: 0, weight: 0 };
    // Nothing is on record beneath a gap either: it is one tile
    if (kids.length === 0 || node.gap) {
      return { ...draft, value: leafValue(node, measure), drillable: kids.length > 0 };
    }
    return { ...draft, value: 0, drillable: true, children: beneath(node, kids) };
  };

  // The classes of a class as drafts, with the leaf of what is filed on it
  const beneath = (node: TreemapNode, kids: TreemapNode[]): Draft[] => {
    const children = order(kids, measure).map((kid) => build(kid));
    if (ownCount(index, node) > 0) {
      const covered = kids.reduce((total, kid) => total + measureOf(kid, measure), 0);
      children.push({
        key: `own:${node.id}`,
        node,
        own: true,
        value: Math.max(0, measureOf(node, measure) - covered),
        real: 0,
        weight: 0,
        drillable: false,
      });
    }
    return children;
  };

  let top: Draft[];
  if (root === null) {
    top = order(index.children.get(null) ?? [], measure).map((node) => build(node));
  } else {
    // A class with nothing beneath it is drawn as itself
    const kids = index.children.get(root.id) ?? [];
    top = kids.length > 0 ? beneath(root, kids) : [build(root)];
  }
  if (
    top.length === 0 ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 2 * CANVAS_PADDING ||
    height <= 2 * CANVAS_PADDING
  ) {
    return { rects: [], root, path, hidden: 0 };
  }

  const total = top.reduce((sum, draft) => sum + sumReal(draft), 0);
  share(top, total > 0 ? total : 1, total * MINIMUM_SHARE);

  // The box itself: not drawn
  const virtual: Draft = {
    key: "",
    node: top[0].node,
    own: false,
    value: 0,
    real: total,
    weight: 0,
    drillable: false,
    children: top,
  };
  const tree = hierarchy<Draft>(virtual, (draft) => draft.children).sum((draft) =>
    draft.children ? 0 : draft.weight,
  );
  const laid = treemap<Draft>()
    .tile(treemapSquarify)
    .size([width, height])
    .round(false)
    .paddingInner((entry) => (entry.depth === 0 ? GROUP_GAP : SIBLING_GAP))
    .paddingTop((entry) => (entry.depth === 0 ? CANVAS_PADDING : FRAME_STRIP))
    .paddingRight((entry) => (entry.depth === 0 ? CANVAS_PADDING : FRAME_PADDING))
    .paddingBottom((entry) => (entry.depth === 0 ? CANVAS_PADDING : FRAME_PADDING))
    .paddingLeft((entry) => (entry.depth === 0 ? CANVAS_PADDING : FRAME_PADDING))(tree);

  // d3 lays a frame smaller than its own padding out as a line on the far
  // side of it, outside the frame. So every rectangle is rounded to whole
  // pixels and clamped into the content box of the frame it sits in; what a
  // frame holds is drawn only where the frame itself is.
  const content = new Map<string, Box>([
    [
      "",
      {
        x0: CANVAS_PADDING,
        y0: CANVAS_PADDING,
        x1: Math.round(width) - CANVAS_PADDING,
        y1: Math.round(height) - CANVAS_PADDING,
      },
    ],
  ]);
  const drawn: { draft: Draft; parentKey: string | null; depth: number; box: Box; inner: boolean }[] =
    [];
  let hidden = 0;
  // Parents come before what they hold
  laid.each((entry) => {
    if (entry.depth === 0 || !entry.parent) {
      return;
    }
    const within = content.get(entry.parent.data.key);
    // The frame it sits in is a tile, or is not drawn
    if (!within) {
      return;
    }
    const draft = entry.data;
    const x0 = clamp(Math.round(entry.x0 ?? 0), within.x0, within.x1);
    const y0 = clamp(Math.round(entry.y0 ?? 0), within.y0, within.y1);
    const box: Box = {
      x0,
      y0,
      x1: clamp(Math.round(entry.x1 ?? 0), x0, within.x1),
      y1: clamp(Math.round(entry.y1 ?? 0), y0, within.y1),
    };
    const boxWidth = box.x1 - box.x0;
    const boxHeight = box.y1 - box.y0;
    if (!(boxWidth >= MIN_DRAWN && boxHeight >= MIN_DRAWN)) {
      hidden += 1;
      return;
    }
    const inner =
      Boolean(draft.children) &&
      entry.depth <= MAX_FRAME_DEPTH &&
      boxWidth >= FRAME_MIN_WIDTH &&
      boxHeight >= FRAME_MIN_HEIGHT;
    if (inner) {
      content.set(draft.key, {
        x0: box.x0 + FRAME_PADDING,
        y0: box.y0 + FRAME_STRIP,
        x1: box.x1 - FRAME_PADDING,
        y1: box.y1 - FRAME_PADDING,
      });
    }
    drawn.push({
      draft,
      parentKey: entry.parent.depth > 0 ? entry.parent.data.key : null,
      depth: entry.depth - 1,
      box,
      inner,
    });
  });

  // The colours are scaled against the largest tile drawn
  const valueOf = (draft: Draft): number =>
    draft.own ? draft.value : measureOf(draft.node, measure);
  const tiles = drawn.filter((entry) => !entry.inner);
  const largestMakers = Math.max(1, ...tiles.map((entry) => entry.draft.node.maker_count));
  const largestValue = Math.max(
    1,
    ...tiles.map((entry) => (entry.draft.node.gap ? 0 : valueOf(entry.draft))),
  );

  const rects: TreemapRect[] = drawn.map(({ draft, parentKey, depth, box, inner }) => {
    const { node } = draft;
    return {
      key: draft.key,
      nodeId: node.id,
      parentKey,
      depth,
      x: box.x0,
      y: box.y0,
      width: box.x1 - box.x0,
      height: box.y1 - box.y0,
      label: draft.own ? OWN_LABEL : node.category.name ?? node.id,
      count: draft.own ? ownCount(index, node) : node.product_count,
      makers: draft.own ? 0 : node.maker_count,
      articles: draft.own ? 0 : node.articles,
      value: valueOf(draft),
      inner,
      drillable: draft.drillable,
      own: draft.own,
      gap: node.gap,
      thin: node.thin,
      other: node.other,
      colour: {
        products: node.gap ? 0 : Math.min(1, valueOf(draft) / largestValue),
        makers: draft.own || node.gap ? 0 : Math.min(1, node.maker_count / largestMakers),
        domestic: draft.own || node.gap ? null : node.domestic_share,
      },
    };
  });
  return { rects, root, path, hidden };
};

// The five tints of a scaled value, 0 to 4
export const TINTS = 5;
export const tintOf = (scaled: number): number =>
  Math.max(0, Math.min(TINTS - 1, Math.floor(scaled * TINTS)));

export const labelFits = (rect: TreemapRect): boolean =>
  rect.inner
    ? rect.width >= FRAME_LABEL_MIN_WIDTH
    : rect.width >= LEAF_LABEL_MIN_WIDTH && rect.height >= LEAF_LABEL_MIN_HEIGHT;

export const countFits = (rect: TreemapRect): boolean =>
  rect.inner ? rect.width >= FRAME_COUNT_MIN_WIDTH : labelFits(rect) && rect.height >= LEAF_COUNT_MIN_HEIGHT;
